/**
 * Utilidad para capturar y preservar TODOS los parámetros de la URL de llegada.
 * Incluye utm_*, gad_*, gclid, y cualquier otro query param presente.
 */

import { buildRouteWithUtms as buildRouteWithCanonicalUtms, buildUtmQueryString } from "../config/whatsappConfig";

const STORAGE_KEY = "utm_tracking_params";
const GCLID_STORAGE_KEY = "gclid";
const GCLID_EXPIRY_MS = 90 * 24 * 60 * 60 * 1000; // 90 días

/**
 * Extrae TODOS los parámetros de la URL (query string)
 */
export const getTrackingParamsFromUrl = (url = window.location.href) => {
  try {
    const urlObj = new URL(url);
    const params = {};
    urlObj.searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  } catch {
    return {};
  }
};

const getParamFromUrl = (param, url = window.location.href) => {
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get(param);
  } catch {
    return null;
  }
};

const getExpiryRecord = (value) => {
  const expiryDate = new Date().getTime() + GCLID_EXPIRY_MS;
  return { value, expiryDate };
};

export const saveGclidFromUrl = (url = window.location.href) => {
  const gclidParam = getParamFromUrl("gclid", url);
  const gclsrcParam = getParamFromUrl("gclsrc", url);
  const isGclsrcValid = !gclsrcParam || gclsrcParam.indexOf("aw") !== -1;

  if (gclidParam && isGclsrcValid) {
    const record = getExpiryRecord(gclidParam);
    try {
      localStorage.setItem(GCLID_STORAGE_KEY, JSON.stringify(record));
    } catch {}
    return record.value;
  }

  return null;
};

export const getSavedGclid = () => {
  try {
    const raw = localStorage.getItem(GCLID_STORAGE_KEY);
    if (!raw) return null;
    const record = JSON.parse(raw);
    const isValid = record?.value && record?.expiryDate && new Date().getTime() < record.expiryDate;
    return isValid ? record.value : null;
  } catch {
    return null;
  }
};

/**
 * Guarda los parámetros de tracking en sessionStorage
 * (se usa al cargar la página, ej. en Footer o App)
 */
export const saveTrackingParams = (url = window.location.href) => {
  const params = getTrackingParamsFromUrl(url);
  // Guardar GCLID con vencimiento (requisito típico para importación offline)
  saveGclidFromUrl(url);
  if (Object.keys(params).length > 0) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
    return params;
  }
  return params;
};

/**
 * Obtiene los parámetros de tracking guardados (o de la URL actual)
 */
export const getSavedTrackingParams = () => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Si no hay gclid en session, intentar restaurarlo desde localStorage (90 días)
      if (!parsed?.gclid) {
        const gclid = getSavedGclid();
        if (gclid) return { ...parsed, gclid };
      }
      return parsed;
    }
  } catch {}
  const fromUrl = getTrackingParamsFromUrl(window.location.href);
  if (!fromUrl?.gclid) {
    const gclid = getSavedGclid();
    if (gclid) return { ...fromUrl, gclid };
  }
  return fromUrl;
};

/** Suborigen por defecto cuando no hay `utm_medium` en la URL (canal de entrada al lead). */
export const CONTACT_ENTRY = {
  FORMULARIO: "formulario_contacto",
  WHATSAPP: "whatsapp",
};

/**
 * Suborigen para CRM/API: usa `utm_medium` si viene en la URL; si no, el punto de contacto (formulario vs WhatsApp).
 */
export const resolveSuborigen = (savedParams, contactEntry) => {
  const medium = savedParams?.utm_medium;
  if (medium != null && String(medium).trim() !== "") return String(medium).trim();
  if (contactEntry != null && String(contactEntry).trim() !== "") return String(contactEntry).trim();
  return "web";
};

/**
 * Retorna el query string con UTMs canónicos y otros params de tracking (gclid, gad_*, etc.).
 */
export const getUtmsQueryString = () => {
  const params = getSavedTrackingParams();
  const utmKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
  const merged = new URLSearchParams(buildUtmQueryString().substring(1));

  Object.entries(params).forEach(([key, value]) => {
    if (utmKeys.includes(key)) return;
    if (value != null && String(value).trim() !== "") {
      merged.set(key, String(value));
    }
  });

  const qs = merged.toString();
  return qs ? `?${qs}` : "";
};

/**
 * Ruta interna del Router con query de tracking preservado.
 * @deprecated Preferir `buildRouteWithUtms` desde `config/whatsappConfig`.
 */
export const buildRouteWithUtms = (route, search) => {
  return buildRouteWithCanonicalUtms(route, search);
};

/**
 * Construye una URL con todos los UTMs de tracking preservados
 * Útil para el mensaje de WhatsApp o cuando se redirige
 */
export const buildUrlWithAllUtms = (baseUrl) => {
  try {
    const url = baseUrl ? new URL(baseUrl) : new URL(window.location.href);
    const params = getSavedTrackingParams();

    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });

    return url.toString();
  } catch {
    return window.location.href;
  }
};
